import React from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Button, 
  Typography,
  Input,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { CloudUpload, GetApp } from '@material-ui/icons';

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(3)
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  uploadSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  input: {
    display: 'none'
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    position: 'relative'
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  icon: {
    marginRight: theme.spacing(1)
  },
  wrapper: {
    position: 'relative',
    display: 'inline-block'
  }
}));

export default function ApplicantUpload({ onApplicantsUploaded, isLoading }) {
  const classes = useStyles();

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        const rows = text.split('\\n');
        const headers = rows[0].split(',').map(header => header.trim());
        
        const applicants = rows.slice(1)
          .filter(row => row.trim())
          .map((row, index) => {
            const values = row.split(',').map(value => value.trim());
            const applicant = {
              id: (Date.now() + index).toString(),
              name: values[headers.indexOf('name')] || '',
              location: values[headers.indexOf('location')] || '',
              landSize: values[headers.indexOf('landSize')] || '',
              crop: values[headers.indexOf('crop')] || '',
              phone: values[headers.indexOf('phone')] || '',
              applicationDate: values[headers.indexOf('applicationDate')] || new Date().toISOString().split('T')[0],
              requestedAmount: values[headers.indexOf('requestedAmount')] || '',
              loanPurpose: values[headers.indexOf('loanPurpose')] || '',
              previousLoans: parseInt(values[headers.indexOf('previousLoans')]) || 0,
              decisionStatus: 'pending',
              ndvi: Math.random() * 0.4 + 0.4,
              yieldForecast: Math.floor(Math.random() * 40 + 45),
              vegetationIndex: Math.floor(Math.random() * 40 + 45),
              overallRisk: Math.floor(Math.random() * 50 + 20),
              climateRisk: Math.floor(Math.random() * 50 + 20),
              soilRisk: Math.floor(Math.random() * 50 + 20),
              marketRisk: Math.floor(Math.random() * 50 + 20),
              historyRisk: Math.floor(Math.random() * 50 + 20),
              aiConfidence: Math.floor(Math.random() * 20 + 75),
              recommendedAction: Math.random() > 0.3 ? 'approve' : 'review'
            };
            return applicant;
          });

        onApplicantsUploaded(applicants);
        alert(`Successfully uploaded ${applicants.length} applicants`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
        alert('Error parsing CSV file. Please check the format and try again.');
      }
    };

    reader.onerror = () => {
      alert('Error reading file');
    };

    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const headers = [
      'name',
      'location',
      'landSize',
      'crop',
      'phone',
      'applicationDate',
      'requestedAmount',
      'loanPurpose',
      'previousLoans'
    ];

    const example = [
      'John Doe',
      'Western Cape, South Africa',
      '4.5 hectares',
      'Maize',
      '+27 71 234 5678',
      '2024-04-26',
      'R 75,000',
      'Seeds and Equipment',
      '1'
    ];

    const csvContent = [
      headers.join(','),
      example.join(',')
    ].join('\\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'applicant-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card className={classes.root}>
      <CardHeader
        title="Applicant Data Upload"
        className={classes.cardHeader}
      />
      <CardContent>
        <div className={classes.uploadSection}>
          <input
            accept=".csv"
            className={classes.input}
            id="csv-upload"
            type="file"
            onChange={handleFileUpload}
            disabled={isLoading}
          />
          <div className={classes.wrapper}>
            <label htmlFor="csv-upload">
              <Button
                variant="contained"
                color="primary"
                component="span"
                className={classes.button}
                disabled={isLoading}
              >
                <CloudUpload className={classes.icon} />
                Upload CSV
                {isLoading && (
                  <CircularProgress
                    size={24}
                    className={classes.buttonProgress}
                  />
                )}
              </Button>
            </label>
          </div>
          <div className={classes.wrapper}>
            <Button
              variant="outlined"
              color="primary"
              onClick={downloadTemplate}
              className={classes.button}
              disabled={isLoading}
            >
              <GetApp className={classes.icon} />
              Download Template
              {isLoading && (
                <CircularProgress
                  size={24}
                  className={classes.buttonProgress}
                />
              )}
            </Button>
          </div>
        </div>
        <Typography variant="body2" color="textSecondary">
          Upload a CSV file containing applicant information. Download the template to see the required format.
        </Typography>
      </CardContent>
    </Card>
  );
}
